import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import {
  Phone, ShieldCheck, MapPin, Users, BadgeCheck, Droplets, Gauge, Filter,
  ArrowRight, Star, Clock, Mail, Linkedin, X, ChevronDown, Zap, Award,
} from 'lucide-react';
import { PublicHeader } from '@/components/public/PublicHeader';
import { BookOnlineForm } from '@/components/public/BookOnlineForm';
import { Reveal, StaggerGroup, StaggerItem } from '@/components/public/Reveal';
import { CountUp } from '@/components/public/CountUp';
import { ScrollProgress } from '@/components/public/ScrollProgress';
import { WaveDivider } from '@/components/public/WaveDivider';
import './public.css';

const LOGO = 'https://customer-assets.emergentagent.com/job_blue-well-crm/artifacts/vyxcu57o_image.png';
const HERO_BG = '/site/hero-main.webp';

const SERVICES = [
  { n: '01', icon: Droplets, title: 'Wells', copy: 'Reliable and clean water supply from deep below — drilled with precision and built to last.', img: '/site/service-wells.webp' },
  { n: '02', icon: Gauge, title: 'Pumps', copy: 'Efficient pump systems for every home or business, professionally installed and serviced.', img: '/site/service-pumps.webp' },
  { n: '03', icon: Filter, title: 'Filtration System', copy: 'Clean, pure water with advanced filtration and water purification solutions.', img: '/site/service-filtration.webp' },
];

const TRUST = [
  { icon: BadgeCheck, label: 'Licensed #9566' },
  { icon: MapPin, label: 'Serving Highlands & nearby counties' },
  { icon: Users, label: 'Family-owned & operated' },
  { icon: ShieldCheck, label: 'Free estimates' },
];

const WHY = [
  { icon: ShieldCheck, text: 'Licensed & insured (#9566)' },
  { icon: Users, text: 'Family-owned & operated' },
  { icon: Zap, text: 'Fast, dependable response' },
  { icon: BadgeCheck, text: 'Free, honest estimates' },
  { icon: Award, text: 'Advanced, modern equipment' },
];

const STATS = [
  { to: 20, suffix: '+', label: 'Years of experience' },
  { to: 1000, suffix: '+', label: 'Wells drilled' },
  { to: 16, suffix: '', label: 'Counties served' },
  { to: 98, suffix: '%', label: 'Customer satisfaction' },
];

const CITIES = [
  'Avon Park', 'Babson Park', 'Bowling Green', 'Frostproof', 'Homeland', 'Lake Placid',
  'Lorida', 'Venus', 'Sebring', 'Ona', 'Wauchula', 'Zolfo Springs',
  'Lake Wales', 'Punta Gorda', 'Arcadia', 'Okeechobee',
];

const GALLERY = [
  '/site/gallery-01-residential.webp',
  '/site/gallery-02-pump-tank.webp',
  '/site/gallery-03-rig-field.webp',
  '/site/gallery-04-solar-setup.webp',
  '/site/gallery-05-solar-setup.webp',
  '/site/gallery-06-wiring.webp',
  '/site/gallery-07-livestock-water.webp',
  '/site/gallery-08-solar-canopy.webp',
  '/site/gallery-09-drill-pipe-yard.webp',
];

// Testimonials hidden until real customer reviews are provided.

const sectionPad = 'py-24 sm:py-32';
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const Glow = ({ className }) => (
  <div className={`absolute rounded-full blur-[120px] pointer-events-none ${className}`} aria-hidden="true" />
);

const ReviewCard = () => null;

export default function PublicHome() {
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState(null);
  const reduce = useReducedMotion();

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div className="bw-public bw-grain min-h-screen text-zinc-200 relative" data-testid="public-home">
      <ScrollProgress />
      <PublicHeader />

      {/* HERO */}
      <section ref={heroRef} id="top" className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: reduce ? 0 : bgY }}>
          <img src={HERO_BG} alt="Well drilling rig at dusk" className={`w-full h-[120%] object-cover ${reduce ? '' : 'ken-burns'}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0F1C] via-[#0A0F1C]/85 to-[#0A0F1C]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1C] via-transparent to-[#0A0F1C]/60" />
        </motion.div>
        <Glow className="w-[640px] h-[640px] bg-[#1FB6E9]/15 top-[10%] left-[8%]" />

        <motion.div style={{ opacity: reduce ? 1 : heroFade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-[#38BDF8] text-xs font-semibold uppercase tracking-widest mb-7"
            >
              <Droplets className="w-3.5 h-3.5" /> Sebring, FL · Licensed #9566
            </motion.span>

            <h1 className="fluid-h1 font-extrabold text-white font-display">
              {['Reliable Well Drilling', 'Services in '].map((line, i) => (
                <motion.span key={i} className="block" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}>
                  {line}{i === 1 && <span className="gradient-text">Sebring, FL</span>}
                </motion.span>
              ))}
              <motion.span className="block" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.34, ease: [0.22, 1, 0.36, 1] }}>
                & Surrounding Areas
              </motion.span>
            </h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="mt-6 text-lg sm:text-xl text-zinc-300/90 max-w-2xl leading-relaxed">
              Comprehensive well pump service, drilling solutions, and advanced water purification systems.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.62 }} className="mt-9 flex flex-col sm:flex-row gap-4">
              <button data-testid="hero-book-btn" onClick={() => scrollTo('book')} className="cta-shimmer group px-8 py-4 rounded-full bg-[#1FB6E9] hover:bg-[#38BDF8] text-[#06121f] font-bold text-base flex items-center justify-center gap-2 shadow-[0_16px_44px_-10px_rgba(31,182,233,0.7)] hover:-translate-y-0.5 transition-all duration-200">
                Book Online <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="tel:+18636580035" data-testid="hero-call-btn" className="px-8 py-4 rounded-full glass hover:border-[#38BDF8]/60 text-white font-semibold text-base flex items-center justify-center gap-2 transition-all duration-200">
                <Phone className="w-4 h-4" /> Call (863) 658-0035
              </a>
            </motion.div>
          </div>

          {/* Social proof glass card */}
          <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.5 }} className="lg:col-span-4 hidden lg:block">
            <div className="glass rounded-2xl p-7 space-y-5">
              <div className="flex items-center gap-3">
                <div className="flex gap-0.5">{[...Array(5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-[#FACC15] text-[#FACC15]" />)}</div>
                <span className="text-white font-bold">4.9</span>
                <span className="text-zinc-500 text-sm">Google rating</span>
              </div>
              <div className="h-px bg-white/10" />
              <div className="grid grid-cols-2 gap-5">
                <div><p className="text-3xl font-extrabold text-white font-display">20+</p><p className="text-xs text-zinc-400 mt-1">Years</p></div>
                <div><p className="text-3xl font-extrabold text-white font-display">1,000+</p><p className="text-xs text-zinc-400 mt-1">Wells drilled</p></div>
                <div><p className="text-3xl font-extrabold text-white font-display">16</p><p className="text-xs text-zinc-400 mt-1">Counties</p></div>
                <div><p className="text-3xl font-extrabold text-white font-display">100%</p><p className="text-xs text-zinc-400 mt-1">Licensed</p></div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <button onClick={() => scrollTo('services')} className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 text-zinc-400 hover:text-[#38BDF8] transition-colors" aria-label="Scroll down">
          <motion.div animate={reduce ? {} : { y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
            <ChevronDown className="w-7 h-7" />
          </motion.div>
        </button>
      </section>

      {/* TRUST STRIP */}
      <section className="relative z-20 border-y border-white/[0.07] bg-[#0B1220]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/[0.06]">
          {TRUST.map((t, i) => (
            <Reveal key={t.label} delay={i * 70} className="flex items-center gap-3 px-4 sm:px-6 py-5">
              <t.icon className="w-5 h-5 text-[#38BDF8] shrink-0" />
              <span className="text-sm font-medium text-zinc-300">{t.label}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className={`${sectionPad} px-5 sm:px-8 relative overflow-hidden`} data-testid="services-section">
        <Glow className="w-[500px] h-[500px] bg-[#1E40AF]/15 -top-20 right-[5%]" />
        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-[#38BDF8] text-sm font-semibold uppercase tracking-widest mb-3">What we do</p>
            <h2 className="fluid-h2 font-bold text-white font-display">Complete water solutions, <span className="gradient-text">start to finish</span></h2>
          </Reveal>
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-7">
            {SERVICES.map((s) => (
              <StaggerItem key={s.title}>
                <div className="group h-full rounded-2xl overflow-hidden glass hover:border-[#1FB6E9]/50 hover:-translate-y-2 transition-all duration-300 hover:shadow-[0_30px_60px_-25px_rgba(31,182,233,0.4)]">
                  <div className="relative h-52 overflow-hidden">
                    <img src={s.img} alt={s.title} loading="lazy" className="duotone w-full h-full object-cover" />
                    <div className="duotone-tint absolute inset-0" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121A2B] via-[#121A2B]/20 to-transparent" />
                    <span className="absolute top-4 right-5 text-5xl font-extrabold text-white/10 font-display group-hover:text-[#38BDF8]/30 transition-colors">{s.n}</span>
                    <div className="absolute bottom-4 left-4 w-12 h-12 rounded-xl bg-[#1FB6E9] flex items-center justify-center shadow-[0_8px_24px_-4px_rgba(31,182,233,0.7)]">
                      <s.icon className="w-6 h-6 text-[#06121f]" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-2 font-display">{s.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">{s.copy}</p>
                    <button onClick={() => scrollTo('book')} className="mt-4 inline-flex items-center gap-1.5 text-[#38BDF8] text-sm font-semibold opacity-0 group-hover:opacity-100 group-hover:gap-2.5 transition-all duration-300">
                      Learn more <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <WaveDivider fill="#0B1220" />

      {/* WHY CHOOSE US */}
      <section className={`${sectionPad} px-5 sm:px-8 bg-[#0B1220] relative overflow-hidden`} data-testid="about-section">
        <Glow className="w-[480px] h-[480px] bg-[#1FB6E9]/10 bottom-0 left-[10%]" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div className="relative">
                <img src="/site/whychooseus.webp" alt="Blue Well Drilling crew at work" loading="lazy" className="rounded-2xl w-full object-cover h-[440px] border border-white/[0.08] shadow-[0_30px_70px_-30px_rgba(0,0,0,0.9)]" />
                <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ type: 'spring', stiffness: 160, damping: 14, delay: 0.2 }} className="absolute -bottom-6 -right-2 sm:right-6 bg-[#1FB6E9] text-[#06121f] rounded-2xl px-6 py-5 shadow-[0_24px_50px_-15px_rgba(31,182,233,0.7)]">
                  <p className="text-2xl font-extrabold font-display leading-none">License</p>
                  <p className="text-lg font-bold">#9566</p>
                </motion.div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <p className="text-[#38BDF8] text-sm font-semibold uppercase tracking-widest mb-3">Why choose us</p>
              <h2 className="fluid-h2 font-bold text-white mb-6 font-display">Your trusted water pump specialist</h2>
              <p className="text-zinc-400 leading-relaxed mb-7">
                As your trusted water pump specialist, we provide top-tier well drilling services with the experience,
                equipment, and professional crew to get the job done right. We are a family-owned company proudly serving
                Sebring, FL and surrounding areas.
              </p>
              <div className="space-y-3.5">
                {WHY.map((w) => (
                  <div key={w.text} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-[#1FB6E9]/15 flex items-center justify-center shrink-0">
                      <w.icon className="w-3.5 h-3.5 text-[#38BDF8]" />
                    </span>
                    <span className="text-zinc-200">{w.text}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* STATS */}
          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 90} className="glass rounded-2xl p-7 text-center">
                <CountUp to={s.to} suffix={s.suffix} className="text-4xl sm:text-5xl font-extrabold gradient-text font-display" />
                <p className="text-sm text-zinc-400 mt-2">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section id="service-area" className={`${sectionPad} px-5 sm:px-8 relative overflow-hidden`} data-testid="service-area-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-[#38BDF8] text-sm font-semibold uppercase tracking-widest mb-3">Where we work</p>
            <h2 className="fluid-h2 font-bold text-white font-display">Proudly serving <span className="gradient-text">Central Florida</span></h2>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <StaggerGroup className="grid grid-cols-2 sm:grid-cols-3 gap-3" stagger={0.04}>
              {CITIES.map((c) => (
                <StaggerItem key={c} y={16}>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl glass hover:border-[#1FB6E9]/50 hover:-translate-y-0.5 transition-all duration-200">
                    <MapPin className="w-3.5 h-3.5 text-[#38BDF8] shrink-0" />
                    <span className="text-sm text-zinc-300">{c}, FL</span>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
            <Reveal delay={120}>
              <div className="rounded-2xl overflow-hidden border border-white/[0.08] shadow-2xl h-[440px] relative">
                <iframe title="Blue Well Drilling service area" src="https://www.google.com/maps?q=Sebring,+FL+33870&z=9&output=embed" className="dark-map w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                <button onClick={() => scrollTo('book')} className="absolute bottom-4 left-4 right-4 py-3 rounded-xl glass text-white font-semibold text-sm hover:bg-[#1FB6E9] hover:text-[#06121f] transition-colors">
                  Check if we serve your area →
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <WaveDivider fill="#0B1220" />

      {/* GALLERY */}
      <section id="gallery" className={`${sectionPad} px-5 sm:px-8 bg-[#0B1220]`} data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-[#38BDF8] text-sm font-semibold uppercase tracking-widest mb-3">Our work</p>
            <h2 className="fluid-h2 font-bold text-white font-display">Recent projects</h2>
          </Reveal>
          <div className="columns-2 md:columns-4 gap-4 [&>*]:mb-4">
            <Reveal>
              <div className="relative block w-full overflow-hidden rounded-xl border border-white/[0.06] break-inside-avoid">
                <video src="/site/clip-water.mp4" autoPlay muted loop playsInline className="duotone w-full object-cover" style={{ aspectRatio: '4 / 3' }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220]/70 via-transparent to-transparent pointer-events-none" />
                <span className="absolute bottom-3 left-3 text-white text-xs font-semibold flex items-center gap-1.5"><Droplets className="w-3.5 h-3.5 text-[#38BDF8]" /> Fresh water, flowing</span>
              </div>
            </Reveal>
            {GALLERY.map((img, i) => (
              <Reveal key={i} delay={(i % 4) * 70}>
                <button data-testid={`gallery-item-${i}`} onClick={() => setLightbox(img)} className="group relative block w-full overflow-hidden rounded-xl border border-white/[0.06] break-inside-avoid">
                  <img src={img} alt={`Project ${i + 1}`} loading="lazy" className="duotone w-full object-cover" style={{ aspectRatio: i % 3 === 0 ? '3 / 4' : '4 / 3' }} />
                  <div className="duotone-tint absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="absolute bottom-3 left-3 text-white text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">View project →</span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials section hidden until real Google reviews are provided */}

      {/* BOOK ONLINE — 2 columns */}
      <section id="book" className={`${sectionPad} px-5 sm:px-8 bg-[#0B1220] border-t border-white/[0.06] relative overflow-hidden`} data-testid="book-section">
        <Glow className="w-[600px] h-[600px] bg-[#1FB6E9]/12 -bottom-40 left-1/2 -translate-x-1/2" />
        <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="text-[#38BDF8] text-sm font-semibold uppercase tracking-widest mb-3">Book online</p>
            <h2 className="fluid-h2 font-bold text-white mb-5 font-display">Request your service appointment</h2>
            <p className="text-zinc-400 leading-relaxed mb-8">Fill out the form and our office will reach out to confirm. Trusted by hundreds of Central Florida homeowners and businesses.</p>
            <div className="space-y-4 mb-8">
              {[
                { icon: Phone, label: 'Call us', value: '(863) 658-0035', href: 'tel:+18636580035' },
                { icon: Mail, label: 'Email', value: 'info@bluewell.pro', href: 'mailto:info@bluewell.pro' },
                { icon: Clock, label: 'Hours', value: 'Mon–Fri 7AM–5PM · Sat 8:30AM–3PM' },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl glass flex items-center justify-center"><c.icon className="w-5 h-5 text-[#38BDF8]" /></div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500">{c.label}</p>
                    {c.href ? <a href={c.href} className="text-white font-medium hover:text-[#38BDF8] transition-colors">{c.value}</a> : <p className="text-white font-medium">{c.value}</p>}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {['Licensed #9566', 'Family-owned', 'Free estimates'].map((b) => (
                <span key={b} className="px-3 py-1.5 rounded-full glass text-xs font-medium text-zinc-300">{b}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <BookOnlineForm />
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden bg-gradient-to-r from-[#1E40AF] to-[#1FB6E9]" data-testid="cta-band">
        <div className="absolute inset-0 bg-[#0A0F1C]/10" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <h3 className="text-2xl sm:text-3xl font-bold text-white font-display max-w-2xl">Ready for reliable water? Book online or call (863) 658-0035.</h3>
          <div className="flex gap-3 shrink-0">
            <button onClick={() => scrollTo('book')} className="px-7 py-3.5 rounded-full bg-[#06121f] text-white font-bold hover:-translate-y-0.5 transition-transform">Book Online</button>
            <a href="tel:+18636580035" className="px-7 py-3.5 rounded-full bg-white/15 backdrop-blur text-white font-semibold border border-white/30 hover:bg-white/25 transition-colors flex items-center gap-2"><Phone className="w-4 h-4" /> Call</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="bg-[#0A0F1C] border-t border-white/[0.07] relative z-10" data-testid="contact-footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO} alt="Blue Well Drilling" className="w-11 h-11 object-contain" />
              <span className="text-white font-bold text-lg font-display">Blue Well Drilling</span>
            </div>
            <p className="text-sm text-zinc-400 leading-relaxed">Family-owned water well drilling, pump service and filtration company based in Sebring, FL. Licensed #9566.</p>
            <a href="https://www.linkedin.com/in/alian-cruz" target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 text-zinc-400 hover:text-[#38BDF8] text-sm transition-colors"><Linkedin className="w-4 h-4" /> LinkedIn</a>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 font-display">Services</h4>
            <ul className="space-y-2.5 text-sm text-zinc-400">
              {['Wells', 'Pumps', 'Filtration System', 'Free estimates'].map((s) => (
                <li key={s}><button onClick={() => scrollTo('services')} className="hover:text-[#38BDF8] transition-colors">{s}</button></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 font-display">Contact & Hours</h4>
            <ul className="space-y-3 text-sm text-zinc-400">
              <li><a href="tel:+18636580035" className="flex items-center gap-2 hover:text-[#38BDF8] transition-colors"><Phone className="w-4 h-4" /> (863) 658-0035</a></li>
              <li><a href="mailto:info@bluewell.pro" className="flex items-center gap-2 hover:text-[#38BDF8] transition-colors"><Mail className="w-4 h-4" /> info@bluewell.pro</a></li>
              <li className="flex items-start gap-2"><Clock className="w-4 h-4 text-[#38BDF8] mt-0.5" /> <span>Mon–Fri 7AM–5PM<br />Sat 8:30AM–3PM · Sun Closed</span></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 font-display">Get started</h4>
            <button onClick={() => scrollTo('book')} className="w-full px-5 py-3 rounded-full bg-[#1FB6E9] hover:bg-[#38BDF8] text-[#06121f] font-bold text-sm mb-3 transition-colors">Book Online</button>
            <button data-testid="footer-employee-access" onClick={() => navigate('/login')} className="w-full px-5 py-3 rounded-full border border-white/15 hover:border-[#38BDF8]/50 text-zinc-300 hover:text-white font-semibold text-sm transition-colors">Employee Access</button>
          </div>
        </div>
        <div className="border-t border-white/[0.06] py-6 text-center text-xs text-zinc-600">
          © {new Date().getFullYear()} Blue Well Drilling, LLC · License #9566 · Sebring, FL · All rights reserved.
        </div>
      </footer>

      {/* LIGHTBOX */}
      {lightbox && (
        <div data-testid="gallery-lightbox" className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setLightbox(null)} aria-label="Close"><X className="w-8 h-8" /></button>
          <motion.img initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} src={lightbox} alt="Project" className="max-w-full max-h-[88vh] rounded-xl object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
